import { useEffect, useRef } from "react";
import { dateToX, parseDate } from "@/lib/gantt-utils";

/**
 * Wires up the "Today" button and the initial-focus behaviour:
 *   - Registers a callback with the parent so the toolbar's Today button can
 *     scroll the gantt to today (or the closest edge if today is outside the
 *     current range).
 *   - On first render, auto-centres the horizontal scroll on today; if today is
 *     outside the range, falls back to `project.start_date`.
 */
export default function useScrollToToday({
  scrollRef,
  range,
  pxPerDay,
  width,
  project,
  registerScrollToToday,
}) {
  useEffect(() => {
    if (!registerScrollToToday) return;
    const fn = () => {
      if (!scrollRef.current) return;
      const t = new Date();
      let x = dateToX(t, range.start, pxPerDay);
      if (x == null) return;
      const viewport = scrollRef.current.clientWidth || 0;
      if (x < 0) x = 0;
      if (x > width) x = width;
      const target = Math.max(0, x - viewport / 2);
      scrollRef.current.scrollTo({ left: target, behavior: "smooth" });
    };
    registerScrollToToday(fn);
    return () => registerScrollToToday(null);
  }, [registerScrollToToday, scrollRef, range, pxPerDay, width]);

  const firstRender = useRef(true);
  useEffect(() => {
    if (!scrollRef.current) return;
    if (!firstRender.current) return;
    firstRender.current = false;
    const viewport = scrollRef.current.clientWidth || 0;
    const todayReal = dateToX(new Date(), range.start, pxPerDay);
    const startX = project.start_date ? dateToX(parseDate(project.start_date), range.start, pxPerDay) : null;
    let target = null;
    if (todayReal != null && todayReal >= 0 && todayReal <= width) target = todayReal;
    else if (startX != null) target = startX;
    else if (todayReal != null) target = Math.max(0, Math.min(width, todayReal));
    if (target != null) {
      scrollRef.current.scrollLeft = Math.max(0, target - viewport / 2);
    }
  }, [scrollRef, project.start_date, range.start, pxPerDay, width]);
}
